/**
 * Wave A · Public Invoice Page — /invoice/:token
 *
 * Loaded from WhatsApp / email share. Read-only, `noindex,nofollow`, no admin
 * data exposed. Provides View / Print / Save-as-PDF via `window.print()`, plus
 * a WhatsApp share button that copies the same public link.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Printer, Share2, Loader2, ShieldCheck, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

const CURRENCY_SYM = { INR: "₹", USD: "$", GBP: "£", EUR: "€", AED: "د.إ", CAD: "C$", AUD: "A$", SGD: "S$" };
const money = (v, code = "INR") => `${CURRENCY_SYM[code] || code} ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_META = {
    draft: { label: "Draft", cls: "bg-slate-200 text-slate-700" },
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    partially_paid: { label: "Partially Paid", cls: "bg-blue-100 text-blue-800 border border-blue-200" },
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    cancelled: { label: "Cancelled", cls: "bg-rose-100 text-rose-800 border border-rose-200" },
};

const CONTACT = {
    phone: "+91 7000530316",
    email: "anandhometutorials@gmail.com",
    website: "www.anandtutorials.com",
    address: "1st Floor, Above HDFC Bank, Vijay Nagar Main Road, Jabalpur, MP 482002",
};

export default function PublicInvoice() {
    const { token } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/invoice/${token}`);
                setInvoice(data);
            } catch (e) {
                setError(e?.response?.status === 404 ? "Invoice not found or link expired." : "Could not load invoice.");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const printIt = () => window.print();
    const shareIt = async () => {
        const url = window.location.href;
        const text = `Anand Tutorials · Invoice ${invoice?.invoice_number || ""} · ${money(invoice?.total, invoice?.currency)}`;
        if (navigator.share) {
            try { await navigator.share({ title: "Invoice", text, url }); return; } catch { }
        }
        try { await navigator.clipboard.writeText(url); alert("Link copied — paste it into WhatsApp / email"); } catch { }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">
                <Helmet>
                    <meta name="robots" content="noindex,nofollow" />
                    <title>Invoice · Anand Tutorials</title>
                </Helmet>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading invoice…
            </div>
        );
    }

    if (error || !invoice) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
                <Helmet>
                    <meta name="robots" content="noindex,nofollow" />
                    <title>Invoice not found · Anand Tutorials</title>
                </Helmet>
                <div className="max-w-md text-center">
                    <h1 className="font-heading text-xl font-bold text-[#0F172A]">Invoice unavailable</h1>
                    <p className="text-sm text-slate-600 mt-2">{error || "Please check the link and try again."}</p>
                    <a href="https://www.anandtutorials.com" className="mt-4 inline-block text-sm font-semibold text-[#1E3A8A] hover:underline">Visit anandtutorials.com →</a>
                </div>
            </div>
        );
    }

    const meta = STATUS_META[invoice.status] || STATUS_META.draft;
    const c = invoice.currency || "INR";

    // V173.2 — Discount visibility. The DISC column + Discount row appear
    // ONLY when at least one line item carries a positive discount OR a
    // flat-level discount was recorded. Zero/empty → every discount-related
    // UI element is completely removed (not just hidden) so the invoice
    // reads cleanly.
    const hasAnyDiscount = (invoice.discount_total || 0) > 0
        || (invoice.line_items || []).some((li) => (li.discount || 0) > 0);

    return (
        <div id="invoice-root" className="min-h-screen bg-slate-100 py-6 sm:py-10 print:bg-white print:py-0">
            <Helmet>
                <meta name="robots" content="noindex,nofollow" />
                <title>{`Invoice ${invoice.invoice_number} · Anand Tutorials`}</title>
            </Helmet>

            {/* Action bar (hidden on print) */}
            <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden" data-testid="public-invoice-actions">
                <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Secure invoice link
                </div>
                <div className="flex gap-2">
                    <button onClick={printIt} data-testid="public-invoice-print"
                        className="inline-flex items-center gap-1 bg-white border border-slate-200 hover:border-[#1E3A8A] text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg">
                        <Printer className="w-4 h-4" /> Print / Save PDF
                    </button>
                    <button onClick={shareIt} data-testid="public-invoice-share"
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-lg">
                        <Share2 className="w-4 h-4" /> Share
                    </button>
                </div>
            </div>

            {/* Invoice document */}
            <div className="invoice-doc max-w-3xl mx-auto bg-white shadow-xl print:shadow-none rounded-xl print:rounded-none border border-slate-200 print:border-0 overflow-hidden" data-testid="public-invoice-doc">
                {/* Header band */}
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0F1E5E] text-white px-6 sm:px-10 py-6 sm:py-8">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Anand Tutorials</div>
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-1">INVOICE</h1>
                            <div className="text-xs opacity-80 mt-2">
                                {CONTACT.website} · {CONTACT.email}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Invoice #</div>
                            <div className="font-mono font-bold text-lg mt-1" data-testid="public-invoice-number">{invoice.invoice_number}</div>
                            <div className={`inline-block mt-2 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${meta.cls}`}>
                                {meta.label}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-6 sm:px-10 py-6 sm:py-8 space-y-6">
                    {/* Bill-to + student details */}
                    <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 text-sm">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Bill to</div>
                            <div className="font-semibold text-[#0F172A]">{invoice.parent_name || "—"}</div>
                            {invoice.parent_email && <div className="text-slate-600 text-xs mt-0.5 break-all">{invoice.parent_email}</div>}
                            {invoice.parent_phone && <div className="text-slate-600 text-xs">{invoice.parent_phone}</div>}
                            {(invoice.city || invoice.country) && (
                                <div className="text-slate-500 text-xs mt-0.5">
                                    {[invoice.city, invoice.country].filter(Boolean).join(", ")}
                                </div>
                            )}
                            {invoice.address && <div className="text-slate-500 text-xs mt-0.5">{invoice.address}</div>}
                        </div>
                        {/* V194 — no student name → no STUDENT heading/dash; class details keep their own small heading. */}
                        {(invoice.student_name?.trim() || invoice.grade || invoice.board || invoice.subjects || invoice.tuition_mode) && (
                            <div data-testid="public-invoice-student-block">
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">
                                    {invoice.student_name?.trim() ? "Student" : "Class details"}
                                </div>
                                {invoice.student_name?.trim() && <div className="font-semibold text-[#0F172A]" data-testid="public-invoice-student-name">{invoice.student_name}</div>}
                                {invoice.grade && <div className="text-slate-600 text-xs mt-0.5">{invoice.grade}</div>}
                                {invoice.board && <div className="text-slate-600 text-xs">{invoice.board}</div>}
                                {invoice.subjects && <div className="text-slate-600 text-xs mt-0.5">{invoice.subjects}</div>}
                                {invoice.tuition_mode && <div className="text-slate-500 text-[11px] mt-0.5 capitalize">Mode: {invoice.tuition_mode.replace(/_/g, " ")}</div>}
                            </div>
                        )}
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Invoice</div>
                            <div className="text-xs text-slate-700 space-y-0.5">
                                {invoice.invoice_date && <div><span className="text-slate-500">Date:</span> {invoice.invoice_date}</div>}
                                {invoice.due_date && <div><span className="text-slate-500">Due:</span> {invoice.due_date}</div>}
                                {invoice.payment_date && <div><span className="text-slate-500">Paid on:</span> {invoice.payment_date}</div>}
                                <div><span className="text-slate-500">Currency:</span> {c} ({CURRENCY_SYM[c] || c})</div>
                            </div>
                        </div>
                    </div>

                    {invoice.description && (
                        <div className="text-sm text-slate-600 border-l-4 border-amber-300 pl-3 py-1 bg-amber-50/40">
                            {invoice.description}
                        </div>
                    )}

                    {/* Line items — V172 smart layout.
                        - Session-based lines show "N sessions × ₹X / session" in Qty + Rate cols.
                        - Monthly lines merge Qty + Rate into "Monthly Fee" (no artificial 1 ×).
                        - Legacy / custom lines keep the generic qty × rate display.
                        - V173.2 — Qty/Rate columns hide when every line is monthly;
                          DISC column hides when NO line has a discount (hasAnyDiscount). */}
                    {(() => {
                        const items = invoice.line_items || [];
                        const allMonthly = items.length > 0 && items.every((li) =>
                            (li.service_type === "home_tuition" && li.billing_type === "monthly")
                        );
                        const showDisc = hasAnyDiscount;
                        return (
                            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="public-invoice-services-table" data-has-discount={showDisc ? "true" : "false"}>
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
                                        <tr>
                                            <th className="text-left px-3 py-2 w-full">Service</th>
                                            {!allMonthly && <th className="text-right px-2 py-2 whitespace-nowrap">Sessions</th>}
                                            {!allMonthly && <th className="text-right px-2 py-2 whitespace-nowrap">Rate</th>}
                                            {showDisc && <th className="text-right px-2 py-2">Disc</th>}
                                            <th className="text-right px-3 py-2">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {items.map((li, i) => {
                                            const isMonthly = li.service_type === "home_tuition" && li.billing_type === "monthly";
                                            const isSessionBased = li.service_type === "live_online" || (li.service_type === "home_tuition" && li.billing_type === "per_session");
                                            const serviceLabel = li.description || (
                                                li.service_type === "live_online" ? "1:1 Live Online Classes" :
                                                li.service_type === "home_tuition" ? "Home Tuitions" : "—"
                                            );
                                            return (
                                                <tr key={i} data-testid={`public-line-${i}`} data-service-type={li.service_type || ""} data-billing-type={li.billing_type || ""}>
                                                    <td className="px-3 py-2.5 text-slate-700">
                                                        <div className="font-semibold text-[#0F172A]">{serviceLabel}</div>
                                                        {isSessionBased && (
                                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                                {Number(li.quantity || 0)} Sessions × {money(li.rate, c)} / Session
                                                            </div>
                                                        )}
                                                        {isMonthly && (
                                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                                Monthly Fee — {money(li.rate, c)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    {!allMonthly && (
                                                        <td className="text-right px-2 py-2.5 text-slate-600 whitespace-nowrap">
                                                            {isMonthly ? <span className="text-slate-300">—</span> : li.quantity}
                                                        </td>
                                                    )}
                                                    {!allMonthly && (
                                                        <td className="text-right px-2 py-2.5 text-slate-600 whitespace-nowrap">
                                                            {isMonthly ? <span className="text-slate-300">—</span> : money(li.rate, c)}
                                                        </td>
                                                    )}
                                                    {showDisc && (
                                                        <td className="text-right px-2 py-2.5 text-slate-600">
                                                            {li.discount ? `- ${money(li.discount, c)}` : ""}
                                                        </td>
                                                    )}
                                                    <td className="text-right px-3 py-2.5 font-semibold text-[#0F172A] whitespace-nowrap">{money(li.amount, c)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()}

                    {/* Totals */}
                    <div className="flex justify-end">
                        <div className="w-full sm:w-72 space-y-1.5 text-sm">
                            <Row k="Subtotal" v={money(invoice.subtotal, c)} />
                            {invoice.discount_total ? <Row k="Discount" v={`- ${money(invoice.discount_total, c)}`} /> : null}
                            {invoice.tax_total ? <Row k="Tax" v={money(invoice.tax_total, c)} /> : null}
                            {invoice.additional_charges ? <Row k="Additional" v={money(invoice.additional_charges, c)} /> : null}
                            <div className="border-t border-slate-200 pt-2 mt-2">
                                <Row k="TOTAL" v={money(invoice.total, c)} big />
                            </div>
                            {invoice.amount_paid ? <Row k="Amount paid" v={money(invoice.amount_paid, c)} /> : null}
                            <div className="border-t border-slate-200 pt-2">
                                <Row k="Balance due" v={money(invoice.balance_due, c)} big tone={invoice.balance_due > 0 ? "amber" : "emerald"} />
                            </div>
                        </div>
                    </div>

                    {/* Payment details */}
                    {(invoice.payment_method || invoice.transaction_id) && (
                        <div className="bg-slate-50 rounded-lg p-4 text-sm">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Payment details</div>
                            {invoice.payment_method && <div><span className="text-slate-500">Method:</span> <span className="capitalize font-semibold text-[#0F172A]">{invoice.payment_method.replace(/_/g, " ")}</span></div>}
                            {invoice.transaction_id && <div><span className="text-slate-500">Ref / Txn ID:</span> <span className="font-mono">{invoice.transaction_id}</span></div>}
                            {invoice.payment_date && <div><span className="text-slate-500">Date:</span> {invoice.payment_date}</div>}
                        </div>
                    )}

                    {/* V172 — Per-invoice Note (parent-facing). Rendered ONLY
                        when the admin actually entered a note; never shows
                        "N/A", a blank box, or an auto-generated placeholder. */}
                    {invoice.invoice_note && invoice.invoice_note.trim() && (
                        <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-4" data-testid="public-invoice-note">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800 mb-2">Note</div>
                            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {invoice.invoice_note}
                            </div>
                        </div>
                    )}

                    {/* Terms — V173.2 category-aware policy template.
                        The invoice snapshot carries its own `terms` field at the time
                        of creation, so editing templates later never mutates historical
                        invoices. We render as a <pre> with wrap so numbered lists survive
                        the round-trip. */}
                    {invoice.terms && (
                        <div data-testid="public-invoice-terms">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Terms & Conditions</div>
                            <pre className="terms-block text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">{invoice.terms.replace(/^\s*terms\s*(and|&)\s*conditions\s*\n+/i, "")}</pre>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="invoice-footer pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
                        {invoice.footer_text && <div className="mb-3 text-slate-700 font-semibold">{invoice.footer_text}</div>}
                        <div>Anand Tutorials · {CONTACT.website} · {CONTACT.email}</div>
                        <div>{CONTACT.phone} · {CONTACT.address}</div>
                        <div className="mt-2 inline-flex items-center gap-1 text-slate-400">
                            <MessageCircle className="w-3 h-3" /> This is a computer-generated invoice; no signature required.
                        </div>
                    </div>
                </div>
            </div>

            {/* V173.2 — Hardened print CSS.
                Even though we moved the invoice OUT of the marketing Layout
                (fixing the 4-page sitemap-leak at the architectural level),
                we also tighten @media print to:
                 1. Hide every root sibling that isn't part of the invoice.
                 2. Collapse padding so the invoice fits one A4 page.
                 3. Use `print-color-adjust: exact` so brand colors survive.
                 4. Avoid orphan/widow table rows splitting mid-page.
                Note: #root is a direct body child; #invoice-root lives
                inside it. We hide the Toaster / Helmet shadows via `body >
                script` etc. and keep the single React tree intact. */}
            <style>{`
                @media print {
                    html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
                    body > *:not(#root) { display: none !important; }
                    .print\\:hidden, [data-print="hide"] { display: none !important; }
                    #invoice-root, #invoice-root * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .print\\:bg-white { background: white !important; }
                    .print\\:py-0 { padding: 0 !important; }
                    .print\\:shadow-none { box-shadow: none !important; }
                    .print\\:border-0 { border: 0 !important; }
                    .print\\:rounded-none { border-radius: 0 !important; }
                    #invoice-root { padding: 0 !important; min-height: 0 !important; }
                    #invoice-root .invoice-doc {
                        max-width: 100% !important;
                        margin: 0 !important;
                        box-shadow: none !important;
                        border: 0 !important;
                        border-radius: 0 !important;
                    }
                    /* Collapse all vertical padding in print so T&C + footer
                       always fit on a single A4 page. Hero band stays tight,
                       body uses 10px vertical rhythm, section gaps shrink. */
                    #invoice-root .invoice-doc > div:first-child { padding: 10px 24px !important; }
                    #invoice-root .invoice-doc > div:last-child { padding: 10px 24px !important; }
                    #invoice-root .invoice-doc .space-y-6 > * + * { margin-top: 8px !important; }
                    #invoice-root h1 { font-size: 18px !important; margin-top: 0 !important; }
                    #invoice-root td, #invoice-root th { padding-top: 5px !important; padding-bottom: 5px !important; }
                    #invoice-root .terms-block {
                        font-size: 10px !important;
                        line-height: 1.3 !important;
                        white-space: pre-wrap;
                        margin: 0 !important;
                    }
                    #invoice-root .invoice-footer {
                        padding-top: 6px !important;
                        margin-top: 6px !important;
                        font-size: 9px !important;
                        line-height: 1.25 !important;
                    }
                    #invoice-root .invoice-footer > div { margin-bottom: 0 !important; }
                    #invoice-root table { page-break-inside: auto; }
                    #invoice-root tr   { page-break-inside: avoid; page-break-after: auto; }
                    #invoice-root h1, #invoice-root h2, #invoice-root h3 { page-break-after: avoid; }
                    #invoice-root [data-testid="public-invoice-terms"] { page-break-inside: avoid; }
                    @page { size: A4; margin: 8mm 10mm 8mm 10mm; }
                }
            `}</style>
        </div>
    );
}

const Row = ({ k, v, big, tone }) => (
    <div className="flex items-baseline justify-between">
        <span className={`${big ? "text-xs font-bold uppercase tracking-wider text-slate-700" : "text-xs text-slate-500"}`}>{k}</span>
        <span className={`${big ? "text-lg font-bold" : "font-semibold"} ${tone === "amber" ? "text-amber-700" : tone === "emerald" ? "text-emerald-700" : "text-[#0F172A]"}`}>{v}</span>
    </div>
);
